import { Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';

import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import styles from './SharedFollowSearchBanner.scss';
import { trackFollowButtonClick } from '../../utils/tracking/searchBrowse/trackFollowSearch';
import { locations } from '../../utils/tracking/shared/followSearchTracking';

import { getFilterValues } from '../../finding/SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { pageTypeConstants } from '../../constants/pageTypeConstants';

const SharedFollowSearchBannerComponent = props => {
    const {
        itemSearch,
        user,
        uriRef,
        showFollowButton,
        followSearchValue,
        onFollowSearchCompleted,
        isCategorySearch,
    } = props;

    if (!showFollowButton) {
        return null;
    }

    const isFollowing = props.isFollowing ?? !!user?.favorites?.edges?.[0]?.node?.serviceId;
    const { appliedFilters = [], pageType } = itemSearch || {};

    let displayName = followSearchValue;
    const categoryL1 = getFilterValues(appliedFilters, 'categoryL1')[0]?.displayName;
    const categoryL2 = getFilterValues(appliedFilters, 'categoryL2')[0]?.displayName;
    const sellerName = getFilterValues(appliedFilters, 'sellerPk')[0]?.displayName;
    let messageType = isCategorySearch ? 'noQuotes' : 'quotes';

    if (
        isCategorySearch &&
        !categoryL2 &&
        pageType === pageTypeConstants.MORE_FROM_SELLER &&
        sellerName
    ) {
        displayName = sellerName;
        if (categoryL1) {
            messageType = 'withCategory';
        }
    }

    return (
        <div data-tn="follow-search-banner" className={styles.container}>
            {isFollowing ? (
                <div className={styles.copy}>
                    <FormattedMessage
                        id="sb.FollowSearch.startedFollowingText"
                        defaultMessage='Check your email for weekly updates in <bold>{messageType, select,
                                quotes {"{name}"}
                                withCategory {{name} {categoryL1}}
                                other {{name}}}</bold>.'
                        values={{
                            messageType,
                            bold: chunks => (
                                <span data-tn="follow-search-value" className={styles.searchTerm}>
                                    {chunks}
                                </span>
                            ),
                            name: displayName,
                            categoryL1,
                        }}
                    />
                </div>
            ) : (
                <Fragment>
                    <div className={styles.title}>
                        <FormattedMessage
                            id="sb.SharedFollowSearchBanner.title"
                            defaultMessage="Get Updated with New Arrivals"
                        />
                    </div>
                    <div className={styles.copy}>
                        <FormattedMessage
                            id="sb.SharedFollowSearchBanner.text"
                            defaultMessage='Save <bold>{messageType, select,
                            quotes {"{name}"}
                            withCategory {{name} {categoryL1}}
                            other {{name}}}</bold>, and we’ll notify you when there are new listings in this category.'
                            values={{
                                messageType,
                                bold: chunks => (
                                    <span
                                        data-tn="follow-search-value"
                                        className={styles.searchTerm}
                                    >
                                        {chunks}
                                    </span>
                                ),
                                name: displayName,
                                categoryL1,
                            }}
                        />
                    </div>
                </Fragment>
            )}
            <SaveSearchButton
                size="medium"
                userId={get(user, 'serviceId') || null}
                user={user}
                page={uriRef}
                name={followSearchValue}
                connectionKey="SharedFollowSearchBanner_favorites"
                onFollowSearchCompleted={() => {
                    // isFollowing value should now be opposite of what it was
                    trackFollowButtonClick({
                        isFollowing: !isFollowing,
                        label: 'bottom of results',
                    });
                    onFollowSearchCompleted();
                }}
                favoritesConnection={get(props, 'user.favorites') || null}
                location={locations.SEARCH_BROWSE}
                itemSearch={itemSearch}
            />
        </div>
    );
};

SharedFollowSearchBannerComponent.defaultProps = {
    onFollowSearchCompleted: () => {},
};

SharedFollowSearchBannerComponent.propTypes = {
    showFollowButton: PropTypes.bool.isRequired,
    isFollowing: PropTypes.bool,
    followSearchValue: PropTypes.string,
    onFollowSearchCompleted: PropTypes.func,
    uriRef: PropTypes.string,
    user: PropTypes.object,
    isCategorySearch: PropTypes.bool.isRequired,
    itemSearch: PropTypes.object,
};

export const SharedFollowSearchBanner = createFragmentContainer(SharedFollowSearchBannerComponent, {
    user: graphql`
        fragment SharedFollowSearchBanner_user on User {
            serviceId
            favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages)
                @connection(key: "SharedFollowSearchBanner_favorites", filters: []) {
                edges {
                    node {
                        serviceId
                    }
                }
                ...SaveSearchButton_favoritesConnection
            }
        }
    `,
    itemSearch: graphql`
        fragment SharedFollowSearchBanner_itemSearch on ItemSearchQueryConnection {
            pageType
            appliedFilters {
                name
                values {
                    displayName
                }
            }
            ...SaveSearchButton_itemSearch
        }
    `,
});
